class GeoLocationComponent extends HTMLElement {
  constructor() {
    super();

    // Bind once for stable references
    this._onRootClick = this._onRootClick.bind(this);
    this._abortController = null;

    // Static selectors to avoid typos
    this.SELECTORS = {
      popup: '.geo-location-wrap',
      flagLink: '.flag-link',
      geoUrl: '.geo-url',
      trigger: '.js-geo-location',
      close: '.geo-close-js'
    };
  }

  connectedCallback() {
    // Cache attributes & elements
    this.sectionId = this.getAttribute('data-section-id') || '';
    this.cookieName = `geo-location${this.sectionId}`;
    this.popup = this.querySelector(this.SELECTORS.popup) || null;
    this.geoUrl = this.popup ? this.popup.querySelector(this.SELECTORS.geoUrl) : null;

    // Optional: override redirect URL via attribute (fallback kept)
    this.nzRedirect = this.getAttribute('data-nz-redirect') || 'https://nz.vanta.store/';

    // Respect existing cookie (if set, do nothing except allow manual triggers)
    this.cookieValue = Cookies.get(this.cookieName);

    // One listener for all clicks inside component (event delegation)
    this.addEventListener('click', this._onRootClick, { passive: true });

    // If cookie not set, start geolocation check
    if (!this.cookieValue) {
      this._trackLocation();
    }
  }

  disconnectedCallback() {
    // Cleanup listeners & pending network work
    this.removeEventListener('click', this._onRootClick);
    if (this._abortController) {
      try { this._abortController.abort(); } catch (_) {}
      this._abortController = null;
    }
  }

  // ---------- UI helpers ----------
  _openPopup() {
    if (!this.popup) return;
    this.popup.classList.add('modal-opened');
    this.popup.setAttribute('aria-hidden', 'false');
  }

  _closePopup() {
    if (!this.popup) return;
    this.popup.classList.remove('modal-opened');
    this.popup.setAttribute('aria-hidden', 'true');

    // Persist dismissal so we don’t nag again
    Cookies.set(this.cookieName, 'opened', { path: '/', expires: 7 });
  }

  _setDestinationHrefFromFlag(flagEl) {
    if (!this.geoUrl || !flagEl) return;
    const href = flagEl.getAttribute('data-href');
    if (href) this.geoUrl.setAttribute('href', href);
  }

  _activateFlag(flagEl) {
    if (!this.popup || !flagEl) return;
    const allFlags = this.popup.querySelectorAll(this.SELECTORS.flagLink);
    allFlags.forEach(f => f.classList.remove('active'));
    flagEl.classList.add('active');
  }

  // ---------- Events (delegated) ----------
  _onRootClick(e) {
    const target = e.target.closest(`${this.SELECTORS.close}, ${this.SELECTORS.trigger}, ${this.SELECTORS.flagLink}`);
    if (!target) return;

    // Close buttons
    if (target.matches(this.SELECTORS.close)) {
      e.preventDefault();
      this._closePopup();
      return;
    }

    // Open triggers
    if (target.matches(this.SELECTORS.trigger)) {
      e.preventDefault();
      this._openPopup();
      return;
    }

    // Flag selection
    if (target.matches(this.SELECTORS.flagLink)) {
      e.preventDefault();
      this._activateFlag(target);
      this._setDestinationHrefFromFlag(target);
      return;
    }
  }

  // ---------- Network / Geo ----------
  async _trackLocation() {
    // Prepare abortable fetch
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    try {
      const res = await fetch('https://ipinfo.io/json', { signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      // Defensive checks
      const country = (data && data.country) ? String(data.country).toUpperCase() : '';

      if (!country) return;

      // Simple rules (expand later if needed)
      if (country === 'DE') {
        this._openPopup();
      } else if (country === 'NZ') {
        // Optional cookie to not re-redirect if user returns quickly
        Cookies.set('gepLocationNZ', 'value', { path: '/', expires: 14 });
        window.location.replace(this.nzRedirect);
      }
    } catch (err) {
      // Ignore aborts; log other errors
      if (err && err.name !== 'AbortError') {
        console.error('[GeoLocationComponent] IP lookup failed:', err);
      }
    } finally {
      this._abortController = null;
    }
  }
}

//customElements.define('geo-location', GeoLocationComponent);
